import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Ban, Check, Plus, Receipt } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { useAuth } from '../contexts/auth'
import { apiError, fmtDate, fmtMoney, type IndustryTenant, type Supplier } from '../lib/industria'
import { PAYABLE_STATUS_LABEL, dueSoon, type PayableRow, type PayableStatus } from '../lib/compras'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'
const STATUSES: PayableStatus[] = ['open', 'paid', 'cancelled']

const BADGE: Record<PayableStatus, 'default' | 'success' | 'warning'> = {
  open: 'warning', paid: 'success', cancelled: 'default',
}

export function IndustriaPagarPage() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [status, setStatus] = useState<string>('open')
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    supplierId: '', tenantId: '', description: '', amount: '', dueDate: '', notes: '',
  })

  const { data: payables = [], isLoading } = useQuery<PayableRow[]>({
    queryKey: ['compras', 'payables', status],
    queryFn: async () => (await api.get('/material-payables', {
      params: { status: status || undefined },
    })).data,
  })

  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['industria', 'suppliers'],
    queryFn: async () => (await api.get('/suppliers')).data,
  })

  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['compras'] })

  const createMutation = useMutation({
    mutationFn: async () => (await api.post('/material-payables', {
      supplierId: form.supplierId,
      tenantId: form.tenantId || null,
      description: form.description.trim(),
      amount: Number(form.amount),
      dueDate: form.dueDate || null,
      notes: form.notes.trim() || null,
    })).data,
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setForm({ supplierId: '', tenantId: '', description: '', amount: '', dueDate: '', notes: '' })
    },
    onError: (e) => setError(apiError(e, 'Não foi possível criar o título')),
  })

  const payMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/material-payables/${id}/pay`, {})).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível baixar o título')),
  })

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/material-payables/${id}/cancel`, {})).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível cancelar o título')),
  })

  const openRows = payables.filter(p => p.status === 'open')
  const totalOpen = openRows.reduce((a, p) => a + Number(p.amount), 0)
  const soon = openRows.filter(p => dueSoon(p.dueDate))

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Contas a pagar</h2>
          <p className="text-sm text-gray-500">
            Títulos abertos no recebimento das compras — é ali que o valor deixa de ser previsão
          </p>
        </div>
        <Button onClick={() => { setError(null); setOpen(true) }}>
          <Plus className="h-4 w-4" /> Título manual
        </Button>
      </div>

      {soon.length > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {soon.length} título(s) vencendo em até 7 dias — {fmtMoney(soon.reduce((a, p) => a + Number(p.amount), 0))}.
        </div>
      )}

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <Card>
        <CardHeader>
          <div>
            <span className="text-sm font-semibold text-gray-900">{payables.length} título(s)</span>
            {status === 'open' && totalOpen > 0 && (
              <span className="ml-2 text-sm text-gray-600">{fmtMoney(totalOpen)} em aberto</span>
            )}
          </div>
          <select
            className="h-9 rounded-lg border border-gray-300 px-2 text-sm"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="">Todos</option>
            {STATUSES.map(s => <option key={s} value={s}>{PAYABLE_STATUS_LABEL[s]}</option>)}
          </select>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : payables.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <Receipt className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">Nenhum título</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Título</th>
                    <th className="px-4 py-2.5 text-left font-medium">Fornecedor</th>
                    <th className="px-4 py-2.5 text-left font-medium">Por conta de</th>
                    <th className="px-4 py-2.5 text-left font-medium">Vencimento</th>
                    <th className="px-4 py-2.5 text-right font-medium">Valor</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {payables.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{p.description}</span>
                        {p.purchaseId && (
                          <Link
                            to={`/industria/compras/${p.purchaseId}`}
                            className="block font-mono text-xs text-blue-600 hover:underline"
                          >
                            {p.purchaseNumber}
                          </Link>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{p.supplierName}</td>
                      <td className="px-4 py-2.5 text-gray-600">{p.tenantName ?? 'WS (próprio)'}</td>
                      <td className="px-4 py-2.5">
                        {p.dueDate ? (
                          <span className={p.status === 'open' && dueSoon(p.dueDate, 0) ? 'font-semibold text-red-600' : 'text-gray-700'}>
                            {fmtDate(p.dueDate)}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                        {p.paidAt && (
                          <span className="block text-xs text-gray-400">pago {fmtDate(p.paidAt)}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {fmtMoney(p.amount)}
                        {p.paidAmount && Number(p.paidAmount) !== Number(p.amount) && (
                          <span className="block text-xs text-gray-400">pago {fmtMoney(p.paidAmount)}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant={BADGE[p.status]}>{PAYABLE_STATUS_LABEL[p.status]}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {p.status === 'open' && (
                          isAdmin ? (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                loading={cancelMutation.isPending}
                                onClick={() => { setError(null); cancelMutation.mutate(p.id) }}
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                loading={payMutation.isPending}
                                onClick={() => { setError(null); payMutation.mutate(p.id) }}
                              >
                                <Check className="h-4 w-4" /> Pagar
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Só admin baixa</span>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title="Título manual" open={open} onClose={() => setOpen(false)}>
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); createMutation.mutate() }}
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Fornecedor</label>
            <select
              className={selectClass}
              value={form.supplierId}
              onChange={e => setForm({ ...form, supplierId: e.target.value })}
              required
            >
              <option value="">Selecione...</option>
              {suppliers.filter(s => s.active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <Input
            label="Descrição"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Valor"
              type="number"
              step="0.01"
              min={0}
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              required
            />
            <Input
              label="Vencimento"
              type="date"
              value={form.dueDate}
              onChange={e => setForm({ ...form, dueDate: e.target.value })}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Por conta de (opcional)</label>
            <select
              className={selectClass}
              value={form.tenantId}
              onChange={e => setForm({ ...form, tenantId: e.target.value })}
            >
              <option value="">WS (próprio)</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <Input label="Observações" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={createMutation.isPending}>Criar título</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
